import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

function ContractGenerator() {
  const { id } = useParams();
  const contentRef = useRef(null);

  const [project, setProject] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfRunning, setPdfRunning] = useState(false);

  const [contractData, setContractData] = useState({
    contractDate: new Date().toLocaleDateString('fa-IR'),
    contractNumber: '',
    projectRequirements: '',
    startDate: '',
    endDate: '',
    totalAmount: '',
    prepayment: '',
    supportMonths: '3'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const projectRes = await api.get(`projects/${id}/`);
        const projectData = projectRes.data;
        setProject(projectData);

        if (projectData.customer) {
          const customerId = typeof projectData.customer === 'object'
            ? projectData.customer.id
            : projectData.customer;
          const customerRes = await api.get(`customers/${customerId}/`);
          setCustomer(customerRes.data);
        }

        setContractData(prev => ({
          ...prev,
          totalAmount: projectData.budget || '',
          startDate: projectData.start_date || '',
          endDate: projectData.end_date || '',
          contractNumber: `CON-${projectData.id}-${Date.now().toString().slice(-4)}`
        }));
      } catch (err) {
        setError('خطا در بارگذاری اطلاعات پروژه');
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchData();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContractData(prev => ({ ...prev, [name]: value }));
  };

  const convertToPersianNumbers = (input) => {
    if (input === null || input === undefined || input === '') return '';
    const persianNumbers = ['۰','۱','۲','۳','۴','۵','۶','۷','۸','۹'];
    return input.toString().replace(/\d/g, d => persianNumbers[Number(d)]);
  };

  const numberToWords = (num) => {
    if (num === null || num === undefined || num === '' || isNaN(num)) return '';
    num = parseInt(String(num).replace(/[, ]/g, ''), 10);
    if (num === 0) return 'صفر افغانی';

    const units = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'];
    const teens = ['ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده'];
    const tens = ['', '', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'];
    const hundreds = ['', 'صد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد'];
    const scales = ['', ' هزار', ' میلیون', ' میلیارد'];

    const threeDigitToWords = (n) => {
      let w = '';
      const h = Math.floor(n / 100);
      const t = Math.floor((n % 100) / 10);
      const u = n % 10;
      if (h) w += hundreds[h];
      if (h && (t || u)) w += ' و ';
      if (t >= 2) {
        w += tens[t];
        if (u) w += ' و ' + units[u];
      } else if (t === 1) {
        w += teens[u];
      } else if (u) {
        w += units[u];
      }
      return w;
    };

    let result = '';
    const groups = [];
    let remaining = Math.abs(num);
    while (remaining > 0) {
      groups.push(remaining % 1000);
      remaining = Math.floor(remaining / 1000);
    }

    for (let i = groups.length - 1; i >= 0; i--) {
      const grp = groups[i];
      if (!grp) continue;
      const words = threeDigitToWords(grp);
      if (result) result += ' و ';
      result += words + (scales[i] ? scales[i] : '');
    }

    if (num < 0) result = 'منفی ' + result;
    return result + ' افغانی';
  };

  const generatePDFWithJsPdf = async () => {
    if (pdfRunning) return;
    setPdfRunning(true);
    setError('');

    try {
      if (document.fonts && document.fonts.ready) await document.fonts.ready;

      const pages = Array.from((contentRef.current || document).querySelectorAll('#contract-content .a4-page'));
      if (!pages.length) {
        throw new Error('No .a4-page elements found inside #contract-content');
      }

      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();

      const safeTitle = (project?.title || 'project').replace(/[^\w\-\s\u0600-\u06FF]/g, '-').slice(0, 40);
      const filename = `contract-${safeTitle}-${contractData.contractNumber || 'contract'}.pdf`;

      for (let i = 0; i < pages.length; i++) {
        const pageEl = pages[i];

        const clone = pageEl.cloneNode(true);
        clone.style.position = 'absolute';
        clone.style.left = '-9999px';
        clone.style.top = '0';
        clone.style.width = '210mm';
        clone.style.height = '297mm';
        clone.style.boxSizing = 'border-box';
        clone.style.padding = '15mm';
        document.body.appendChild(clone);

        const scale = 2;

        const canvas = await html2canvas(clone, {
          scale: scale,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          scrollY: -window.scrollY,
          windowWidth: clone.scrollWidth,
          width: clone.scrollWidth,
          height: clone.scrollHeight
        });

        if (clone && clone.parentNode) clone.parentNode.removeChild(clone);

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const imgProps = pdf.getImageProperties(imgData);

        const renderedHeight = (imgProps.height * pdfW) / imgProps.width;

        if (i > 0) pdf.addPage();

        if (renderedHeight <= pdfH) {
          pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, renderedHeight);
        } else {
          const renderedWidth = (imgProps.width * pdfH) / imgProps.height;
          const x = (pdfW - renderedWidth) / 2;
          pdf.addImage(imgData, 'JPEG', x, 0, renderedWidth, pdfH);
        }
      }

      pdf.save(filename);
    } catch (err) {
      setError('خطا در تولید PDF — ' + (err.message || 'نامعلوم'));
    } finally {
      setPdfRunning(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[300px]">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-600 text-lg">{error}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4" style={{ fontFamily: "'Amiri', serif" }}>
      {/* Header / controls */}
      <div className="max-w-4xl mx-auto mb-6 no-print">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/projects" className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200">
              ←
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">قرارداد پروژه</h1>
              <p className="text-gray-600">ایجاد قرارداد برای: {project?.title}</p>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={generatePDFWithJsPdf}
              disabled={pdfRunning}
              className={`bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition flex items-center ${pdfRunning ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {pdfRunning ? 'در حال ساخت PDF...' : 'دانلود PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Input Form */}
      <div className="max-w-4xl mx-auto mb-6 no-print">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">اطلاعات قرارداد</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">شماره قرارداد</label>
              <input
                type="text"
                name="contractNumber"
                value={contractData.contractNumber}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 text-left"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">تاریخ قرارداد</label>
              <input
                type="date"
                name="contractDate"
                value={contractData.contractDate}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 text-left"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">مبلغ کل قرارداد (افغانی)</label>
              <input
                type="number"
                name="totalAmount"
                value={contractData.totalAmount}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 text-left"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">پیش پرداخت (افغانی)</label>
              <input
                type="number"
                name="prepayment"
                value={contractData.prepayment}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 text-left"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">تاریخ شروع</label>
              <input
                type="date"
                name="startDate"
                value={contractData.startDate}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 text-left"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">تاریخ پایان</label>
              <input
                type="date"
                name="endDate"
                value={contractData.endDate}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 text-left"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">نیازهای اساسی پروژه</label>
              <textarea
                name="projectRequirements"
                value={contractData.projectRequirements}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 text-right h-32"
                placeholder="شرح کامل نیازهای پروژه..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contract Content */}
      <div ref={contentRef} id="contract-content" className="contract-container">
        {/* Page 1 */}
        <div className="a4-page page-1">
          <div className="page-content">
            {/* Header Section */}
            <div className="header-section text-center mb-6">
              <div className="company-header mb-3">
                <h1 className="company-name">شرکت توسعه نرم‌افزاری دیویکسا</h1>
                <div className="company-subtitle">Devixa Software Development Company</div>
              </div>
              <div className="contract-header">
                <h2 className="contract-title">قرارداد توسعه و پیاده‌سازی نرم‌افزار</h2>
                <div className="contract-subtitle">Software Development and Implementation Contract</div>
              </div>
            </div>

            {/* Contract Info */}
            <div className="contract-info-section mb-5">
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">شماره قرارداد:</span>
                  <span className="info-value">{contractData.contractNumber}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">تاریخ تنظیم:</span>
                  <span className="info-value">{contractData.contractDate}</span>
                </div>
                <div className="info-item full-width">
                  <span className="info-label">عنوان پروژه:</span>
                  <span className="info-value">{project?.title}</span>
                </div>
              </div>
            </div>

            {/* Parties Section */}
            <div className="parties-section mb-5">
              <h3 className="section-title">طرفین قرارداد</h3>
              
              <div className="party-card">
                <div className="party-header">طرف اول: سفارش‌دهنده</div>
                <div className="party-details">
                  <div className="detail-row">
                    <span className="detail-label">نام/نام شرکت:</span>
                    <span className="detail-value">{customer?.name || '..............................'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">آدرس:</span>
                    <span className="detail-value">{customer?.address || '..............................'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">شماره تماس:</span>
                    <span className="detail-value">{customer?.phone || '..............................'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">شماره تذکره:</span>
                    <span className="detail-value">{customer?.tazkara_number || '..............................'}</span>
                  </div>
                </div>
              </div>

              <div className="party-card">
                <div className="party-header">طرف دوم: پیمانکار (شرکت دیویکسا)</div>
                <div className="party-details">
                  <div className="detail-row">
                    <span className="detail-label">نام شرکت:</span>
                    <span className="detail-value">شرکت توسعه نرم‌افزار دیویکسا</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">آدرس دفتر:</span>
                    <span className="detail-value">کوتاه سنگی، گوایی دواخانه، کابل مارکیت، منزل سوم، دفتر ۵۲</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">شماره تماس:</span>
                    <span className="detail-value">۰۷۹۱۴۹۸۰۸۲ - ۰۷۷۷۴۵۴۸۲</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Preamble */}
            <div className="preamble-section mb-4">
              <p className="preamble-text">
                این قرارداد به منظور تعیین شرایط و ضوابط همکاری بین طرفین در زمینه طراحی، توسعه و پیاده‌سازی نرم‌افزار منعقد می‌گردد. طرفین با امضای این قرارداد کلیه شرایط و مفاد مندرج در آن را قبول و متعهد به اجرای دقیق آن می‌باشند.
              </p>
            </div>

            {/* Articles */}
            <div className="articles-section">
              <div className="article">
                <h3 className="article-title">ماده ۱ - موضوع قرارداد</h3>
                <p className="article-content">
                  موضوع این قرارداد عبارت است از طراحی، توسعه، پیاده‌سازی، تست و تحویل نرم‌افزار/وبسایت/اپلیکیشن مورد نیاز سفارش‌دهنده مطابق با مشخصات فنی، نیازمندی‌ها و مستندات توافق‌شده بین طرفین که به پیوست این قرارداد می‌باشد.
                </p>
              </div>

              <div className="article">
                <h3 className="article-title">ماده ۲ - مدت قرارداد</h3>
                <p className="article-content">
                  مدت اجرای این قرارداد از تاریخ <strong className="highlight">{contractData.startDate || '..............'}</strong> آغاز و تا تاریخ <strong className="highlight">{contractData.endDate || '..............'}</strong> به پایان می‌رسد. در صورت نیاز به تمدید مدت قرارداد، طرفین موظفند حداقل ۱۵ روز قبل از تاریخ پایان، درخواست کتبی خود را ارائه نمایند.
                </p>
              </div>

              <div className="article">
                <h3 className="article-title">ماده ۳ - مبلغ قرارداد و شرایط پرداخت</h3>
                <ol className="article-list">
                  <li>
                    مبلغ کل قرارداد: <strong>{contractData.totalAmount ? convertToPersianNumbers(contractData.totalAmount) : '..............'}</strong> افغانی
                    <br />
                    <em>(به حروف: {contractData.totalAmount ? numberToWords(parseInt(contractData.totalAmount || 0, 10)) : '..............'})</em>
                  </li>
                  <li>
                    شرایط پرداخت:
                    <ul className="sublist">
                      <li>پیش‌پرداخت: مبلغ <strong>{contractData.prepayment ? convertToPersianNumbers(contractData.prepayment) : '..............'}</strong> افغانی به صورت نقدی/حواله در زمان امضای قرارداد</li>
                      <li>پرداخت‌های مرحله‌ای: بر اساس پیشرفت کار و تحویل بخش‌های توافق‌شده طبق برنامه زمان‌بندی</li>
                      <li>تسویه نهایی: پس از تحویل کامل پروژه و تأیید نهایی توسط سفارش‌دهنده</li>
                    </ul>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Page 2 */}
        <div className="a4-page page-2">
          <div className="page-content">
            <div className="articles-section">
              <div className="article">
                <h3 className="article-title">ماده ۴ - تعهدات شرکت دیویکسا</h3>
                <ol className="article-list">
                  <li>انجام کلیه مراحل طراحی، توسعه و پیاده‌سازی مطابق با معیارهای فنی و استانداردهای روز نرم‌افزار</li>
                  <li>تحویل پروژه در چارچوب زمانی توافق‌شده و طبق برنامه زمان‌بندی مشخص‌شده</li>
                  <li>حفظ محرمانگی اطلاعات، اسناد و داده‌های مربوط به سفارش‌دهنده</li>
                  <li>ارائه مستندات فنی و کاربری کامل پروژه پس از تحویل</li>
                  <li>ارائه خدمات پشتیبانی فنی و رفع اشکال به مدت <strong>{contractData.supportMonths}</strong> ماه پس از تحویل نهایی پروژه</li>
                  <li>آموزش کاربران نهایی و پرسنل مربوطه سفارش‌دهنده</li>
                </ol>
              </div>

              <div className="article">
                <h3 className="article-title">ماده ۵ - تعهدات سفارش‌دهنده</h3>
                <ol className="article-list">
                  <li>فراهم‌سازی به موقع اطلاعات، داده‌ها، محتوا و مستندات مورد نیاز</li>
                  <li>پرداخت به موقع مبالغ طبق شرایط و برنامه زمان‌بندی مشخص‌شده در قرارداد</li>
                  <li>همکاری فعال در مراحل مختلف پروژه از جمله تحلیل، تست و ارائه بازخورد</li>
                  <li>تعیین نماینده تام‌الاختیار برای هماهنگی و تصمیم‌گیری‌های لازم</li>
                  <li>فراهم‌سازی دسترسی‌ها و امکانات لازم برای انجام مراحل تست و پیاده‌سازی</li>
                </ol>
              </div>

              <div className="article">
                <h3 className="article-title">ماده ۶ - مالکیت معنوی و حقوقی</h3>
                <p className="article-content">
                  پس از پرداخت کامل مبلغ قرارداد، کلیه حقوق مادی و معنوی پروژه نهایی شده به سفارش‌دهنده منتقل می‌شود. شرکت دیویکسا حق دارد از محصول نهایی صرفاً به عنوان نمونه کار در پورتفولیو و فعالیت‌های تبلیغاتی خود (بدون افشای اطلاعات محرمانه و اسرار تجاری سفارش‌دهنده) استفاده نماید.
                </p>
              </div>

              <div className="article">
                <h3 className="article-title">ماده ۷ - تحویل و پذیرش نهایی</h3>
                <p className="article-content">
                  پروژه پس از تکمیل، جهت تست و بازبینی نهایی در اختیار سفارش‌دهنده قرار خواهد گرفت. سفارش‌دهنده موظف است حداکثر ظرف ۱۰ روز کاری نظر نهایی خود را اعلام نماید. در صورت عدم اعلام نظر در مهلت مقرر، پروژه به صورت خودکار پذیرفته شده تلقی می‌گردد.
                </p>
              </div>

              <div className="article">
                <h3 className="article-title">ماده ۸ - فسخ قرارداد</h3>
                <p className="article-content">
                  هر یک از طرفین در صورت عدم اجرای تعهدات توسط طرف مقابل، می‌تواند پس از ارسال اخطار کتبی و دادن مهلت حداقل ۱۵ روز برای رفع نقص، قرارداد را فسخ نماید. در صورت فسخ قرارداد به دلیل قصور سفارش‌دهنده، مبالغ پردختی مسترد نخواهد شد. در صورت فسخ به دلیل قصور پیمانکار، کلیه مبالغ دریافتی به علاوه خسارات وارده مسترد می‌گردد.
                </p>
              </div>

              <div className="article">
                <h3 className="article-title">ماده ۹ - فورس ماژور</h3>
                <p className="article-content">
                  در صورت بروز حوادث قهری و خارج از اراده طرفین از قبیل سیل، زلزله، جنگ و سایر موارد فورس ماژور که مانع اجرای تعهدات گردد، طرف مسئول موظف است ظرف ۷۲ ساعت وقوع حادثه، مراتب را کتباً به طرف دیگر اطلاع دهد. در این صورت مهلت اجرای تعهدات به مدت زمان تأثیر حادثه تمدید می‌شود.
                </p>
              </div>

              <div className="article">
                <h3 className="article-title">ماده ۱۰ - حل اختلاف</h3>
                <p className="article-content">
                  کلیه اختلافات ناشی از تفسیر و اجرای این قرارداد، در مرحله اول از طریق مذاکره و سازش مستقیم بین طرفین حل‌وفصل خواهد شد. در صورت عدم حصول توافق، موضوع از طریق مراجع قضایی صالحه در جمهوری اسلامی افغانستان پیگیری می‌شود.
                </p>
              </div>

              <div className="article">
                <h3 className="article-title">ماده ۱۱ - الحاقیه و اصلاحات</h3>
                <p className="article-content">
                  هر گونه تغییر، اصلاح و الحاقیه به این قرارداد باید به صورت کتبی و با امضای طرفین صورت پذیرد. این الحاقیه‌ها جزئی لاینفک از قرارداد اصلی محسوب می‌شوند.
                </p>
              </div>

              <div className="articles-section">
              <div className="article">
                <h3 className="article-title">ماده ۱۲ - نسخ قرارداد</h3>
                <p className="article-content">
                  این قرارداد در ۳ نسخه که دارای اعتبار یکسان می‌باشند، تنظیم و توسط طرفین امضاء شده است. یک نسخه نزد سفارش‌دهنده، یک نسخه نزد پیمانکار و نسخه سوم برای امور archival نگهداری می‌شود.
                </p>
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* Page 3 */}
        <div className="a4-page page-3">
          <div className="page-content">
            

            {/* Requirements Section - Expanded to fill page */}
            <div className="requirements-section" style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
              <h3 className="requirements-title">پیوست شماره ۱: مشخصات و نیازمندی‌های پروژه</h3>
              <div className="requirements-content" style={{flex: 1}}>
                {contractData.projectRequirements ? (
                  <div className="requirements-text">{contractData.projectRequirements}</div>
                ) : (
                  <div className="placeholder-lines">
                    {[...Array(25)].map((_, i) => (
                      <div key={i} className="placeholder-line" />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page 4 - Signatures */}
        <div className="a4-page page-4">
          <div className="page-content">
            <div className="signatures-section">
              <h3 className="signatures-title">امضاء و مهر طرفین قرارداد</h3>
              
              <div className="signatures-container">
                <div className="signature-box">
                  <div className="signature-header">
                    <p className="signature-party">طرف اول: سفارش‌دهنده</p>
                  </div>
                  <div className="signature-field">
                    <div className="signature-line"></div>
                    <p className="signature-label">امضاء و مهر</p>
                  </div>
                  <div className="signature-info">
                    <p><strong>نام:</strong> {customer?.name || '..............................'}</p>
                    <p><strong>سمت:</strong> ..............................</p>
                    <p><strong>تاریخ:</strong> ..............................</p>
                  </div>
                </div>

                <div className="signature-box">
                  <div className="signature-header">
                    <p className="signature-party">طرف دوم: شرکت دیویکسا</p>
                  </div>
                  <div className="signature-field">
                    <div className="signature-line"></div>
                    <p className="signature-label">امضاء و مهر</p>
                  </div>
                  <div className="signature-info">
                    <p><strong>نام:</strong> شرکت توسعه نرم‌افزار دیویکسا</p>
                    <p><strong>سمت:</strong> مدیر عامل</p>
                    <p><strong>تاریخ:</strong> ..............................</p>
                  </div>
                </div>
              </div>

              <div className="witness-section">
                <h4 className="witness-title">شاهدین</h4>
                <div className="witness-container">
                  <div className="witness-box">
                    <div className="witness-line"></div>
                    <p className="witness-label">امضاء شاهد اول</p>
                    <p className="witness-name">نام: ..............................</p>
                  </div>
                  <div className="witness-box">
                    <div className="witness-line"></div>
                    <p className="witness-label">امضاء شاهد دوم</p>
                    <p className="witness-name">نام: ..............................</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Styles */}
      <style jsx>{`
        .contract-container { 
          max-width: 210mm; 
          margin: 0 auto;
          font-family: 'Amiri', serif;
        }
        
        .a4-page {
          width: 210mm;
          min-height: 297mm;
          height: 297mm;
          background: white;
          margin: 0 auto 10px auto;
          padding: 15mm;
          box-sizing: border-box;
          direction: rtl;
          text-align: right;
          display: block;
          page-break-after: always;
          break-after: page;
          position: relative;
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
          font-family: 'Amiri', serif;
        }
        
        .a4-page:last-child { 
          page-break-after: auto; 
          break-after: auto; 
        }
        
        .page-content {
          height: 100%;
          display: flex;
          flex-direction: column;
          font-family: 'Amiri', serif;
        }
        
        /* Header Styles */
        .header-section {
          border-bottom: 2px solid #2c5aa0;
          padding-bottom: 4mm;
          margin-bottom: 5mm;
          font-family: 'Amiri', serif;
        }
        
        .company-name {
          font-size: 22pt;
          font-weight: bold;
          color: #2c5aa0;
          margin-bottom: 1mm;
          line-height: 1.2;
          font-family: 'Amiri', serif;
        }
        
        .company-subtitle {
          font-size: 10pt;
          color: #666;
          margin-bottom: 3mm;
          line-height: 1.2;
          font-family: 'Amiri', serif;
        }
        
        .contract-title {
          font-size: 16pt;
          font-weight: bold;
          color: #333;
          margin-bottom: 1mm;
          line-height: 1.2;
          font-family: 'Amiri', serif;
        }
        
        .contract-subtitle {
          font-size: 9pt;
          color: #666;
          line-height: 1.2;
          font-family: 'Amiri', serif;
        }
        
        /* Contract Info */
        .contract-info-section {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 4px;
          padding: 3mm;
          margin-bottom: 5mm;
          font-family: 'Amiri', serif;
        }
        
        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2mm;
          font-family: 'Amiri', serif;
        }
        
        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1mm 0;
          font-family: 'Amiri', serif;
        }
        
        .full-width {
          grid-column: 1 / -1;
        }
        
        .info-label {
          font-weight: bold;
          color: #333;
          font-size: 9pt;
          font-family: 'Amiri', serif;
        }
        
        .info-value {
          color: #2c5aa0;
          font-weight: 600;
          font-size: 9pt;
          font-family: 'Amiri', serif;
        }
        
        /* Parties Section */
        .section-title {
          font-size: 13pt;
          font-weight: bold;
          color: #2c5aa0;
          border-bottom: 1px solid #ddd;
          padding-bottom: 2mm;
          margin-bottom: 3mm;
          font-family: 'Amiri', serif;
        }
        
        .party-card {
          background: white;
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 2mm;
          margin-bottom: 2mm;
          font-family: 'Amiri', serif;
        }
        
        .party-header {
          font-weight: bold;
          color: #333;
          margin-bottom: 1mm;
          font-size: 10pt;
          font-family: 'Amiri', serif;
        }
        
        .detail-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.5mm;
          font-family: 'Amiri', serif;
        }
        
        .detail-label {
          font-weight: 600;
          color: #555;
          font-size: 9pt;
          min-width: 100px;
          font-family: 'Amiri', serif;
        }
        
        .detail-value {
          color: #333;
          font-size: 9pt;
          flex: 1;
          text-align: left;
          font-family: 'Amiri', serif;
        }
        
        /* Articles */
        .articles-section {
          flex: 1;
          font-family: 'Amiri', serif;
        }
        
        .article {
          margin-bottom: 3mm;
          page-break-inside: avoid;
          break-inside: avoid;
          font-family: 'Amiri', serif;
        }
        
        .article-title {
          font-size: 11pt;
          font-weight: bold;
          color: #2c5aa0;
          margin-bottom: 1mm;
          background: #f8f9fa;
          padding: 1mm;
          border-right: 3px solid #2c5aa0;
          font-family: 'Amiri', serif;
        }
        
        .article-content {
          font-size: 9pt;
          line-height: 1.7;
          text-align: justify;
          color: #333;
          margin-bottom: 1mm;
          font-family: 'Amiri', serif;
        }
        
        .article-list {
          font-size: 9pt;
          line-height: 1.7;
          color: #333;
          padding-right: 3mm;
          margin: 1mm 0;
          font-family: 'Amiri', serif;
        }
        
        .sublist {
          list-style-type: disc;
          margin-right: 3mm;
          margin-top: 0.5mm;
          margin-bottom: 0.5mm;
          font-family: 'Amiri', serif;
        }
        
        .highlight {
          background: #fff3cd;
          padding: 1px 3px;
          border-radius: 2px;
          font-family: 'Amiri', serif;
        }
        
        /* Requirements */
        .requirements-section {
          margin-top: 3mm;
          font-family: 'Amiri', serif;
        }
        
        .requirements-title {
          font-size: 11pt;
          font-weight: bold;
          color: #2c5aa0;
          margin-bottom: 2mm;
          text-align: center;
          font-family: 'Amiri', serif;
        }
        
        .requirements-content {
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 3mm;
          min-height: 180mm;
          background: #fdfdfd;
          flex: 1;
          font-family: 'Amiri', serif;
        }
        
        .requirements-text {
          font-size: 9pt;
          line-height: 1.7;
          text-align: justify;
          white-space: pre-wrap;
          color: #333;
          height: 100%;
          font-family: 'Amiri', serif;
        }
        
        .placeholder-lines {
          display: flex;
          flex-direction: column;
          gap: 3mm;
          height: 100%;
          font-family: 'Amiri', serif;
        }
        
        .placeholder-line {
          height: 1.2em;
          border-bottom: 1px dashed #ccc;
          background: linear-gradient(90deg, transparent 95%, #f0f0f0 95%);
          flex-shrink: 0;
          font-family: 'Amiri', serif;
        }
        
        /* Signatures */
        .signatures-section {
          margin-top: 6mm;
          font-family: 'Amiri', serif;
        }
        
        .signatures-title {
          font-size: 13pt;
          font-weight: bold;
          color: #2c5aa0;
          text-align: center;
          margin-bottom: 5mm;
          border-bottom: 1px solid #ddd;
          padding-bottom: 2mm;
          font-family: 'Amiri', serif;
        }
        
        .signatures-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8mm;
          margin-bottom: 6mm;
          font-family: 'Amiri', serif;
        }
        
        .signature-box {
          text-align: center;
          font-family: 'Amiri', serif;
        }
        
        .signature-header {
          margin-bottom: 3mm;
          font-family: 'Amiri', serif;
        }
        
        .signature-party {
          font-weight: bold;
          color: #333;
          font-size: 10pt;
          font-family: 'Amiri', serif;
        }
        
        .signature-field {
          margin-bottom: 2mm;
          font-family: 'Amiri', serif;
        }
        
        .signature-line {
          height: 1px;
          background: #333;
          margin: 3mm 0;
          position: relative;
          font-family: 'Amiri', serif;
        }
        
        .signature-line::after {
          content: '';
          position: absolute;
          top: -6px;
          right: 0;
          left: 0;
          height: 12px;
          background: linear-gradient(90deg, transparent 0%, #333 50%, transparent 100%);
          font-family: 'Amiri', serif;
        }
        
        .signature-label {
          font-size: 8pt;
          color: #666;
          margin-top: 1mm;
          font-family: 'Amiri', serif;
        }
        
        .signature-info {
          text-align: center;
          font-size: 8pt;
          color: #333;
          font-family: 'Amiri', serif;
        }
        
        .signature-info p {
          margin: 0.5mm 0;
          font-family: 'Amiri', serif;
        }
        
        /* Witness Section */
        .witness-section {
          margin-top: 4mm;
          border-top: 1px dashed #ddd;
          padding-top: 3mm;
          font-family: 'Amiri', serif;
        }
        
        .witness-title {
          font-size: 10pt;
          font-weight: bold;
          color: #333;
          text-align: center;
          margin-bottom: 3mm;
          font-family: 'Amiri', serif;
        }
        
        .witness-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 5mm;
          font-family: 'Amiri', serif;
        }
        
        .witness-box {
          text-align: center;
          font-family: 'Amiri', serif;
        }
        
        .witness-line {
          height: 1px;
          background: #666;
          margin: 2mm 0;
          font-family: 'Amiri', serif;
        }
        
        .witness-label {
          font-size: 7pt;
          color: #666;
          font-family: 'Amiri', serif;
        }
        
        .witness-name {
          font-size: 7pt;
          color: #333;
          margin-top: 0.5mm;
          font-family: 'Amiri', serif;
        }
        
        /* No print styles for main view */
        .no-print {
          display: block;
        }
      `}</style>
    </div>
  );
}

export default ContractGenerator;